import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Traveler,
  CreateTravelerRequest,
  UpdateTravelerRequest,
  PersonTypes,
  PersonType,
  LicenseClasses,
  LicenseClass,
  getPersonTypeInfo,
  getLicenseClassInfo
} from '../../types/traveler';

interface TravelerFormProps {
  traveler?: Traveler;
  onSave: (data: CreateTravelerRequest | UpdateTravelerRequest) => void;
  onCancel: () => void;
  loading?: boolean;
}

const TravelerForm: React.FC<TravelerFormProps> = ({
  traveler,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    firstName: traveler?.firstName || '',
    lastName: traveler?.lastName || '',
    personType: traveler?.personType || PersonTypes.PLAYER,
    licenseClasses: traveler?.licenseClasses || [],
    licenseExpiry: traveler?.licenseExpiry ? traveler.licenseExpiry.split('T')[0] : '',
    notes: traveler?.notes || '',
    isActive: traveler?.isActive ?? true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!traveler;
  const isDriver = formData.personType === PersonTypes.DRIVER;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Vorname ist erforderlich';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Nachname ist erforderlich';
    }

    if (!formData.personType) {
      newErrors.personType = 'Personentyp ist erforderlich';
    }

    if (isDriver && formData.licenseClasses.length === 0) {
      newErrors.licenseClasses = 'Fahrer müssen mindestens eine Führerscheinklasse haben';
    }

    if (isDriver && formData.licenseExpiry && new Date(formData.licenseExpiry) < new Date()) {
      newErrors.licenseExpiry = 'Führerschein ist bereits abgelaufen';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      personType: formData.personType,
      licenseClasses: isDriver ? formData.licenseClasses : [],
      licenseExpiry: isDriver && formData.licenseExpiry ? formData.licenseExpiry : undefined,
      notes: formData.notes.trim() || undefined,
      ...(isEditing && { isActive: formData.isActive })
    };

    onSave(submitData);
  };

  const handleLicenseClassChange = (licenseClass: LicenseClass, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      licenseClasses: checked 
        ? [...prev.licenseClasses, licenseClass]
        : prev.licenseClasses.filter(lc => lc !== licenseClass)
    }));
  };

  const handlePersonTypeChange = (personType: PersonType) => {
    setFormData(prev => ({
      ...prev,
      personType,
      // Clear license data when switching away from driver
      ...(personType !== PersonTypes.DRIVER && {
        licenseClasses: [],
        licenseExpiry: ''
      })
    }));
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {isEditing ? 'Traveler bearbeiten' : 'Neuen Traveler erstellen'}
          </h2>
          <button onClick={onCancel} style={closeButtonStyle} disabled={loading}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          {/* Basic Information */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Grunddaten</h3>
            
            <div style={rowStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Vorname *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  style={errors.firstName ? errorInputStyle : inputStyle}
                  disabled={loading}
                  placeholder="Vorname eingeben"
                />
                {errors.firstName && <span style={errorTextStyle}>{errors.firstName}</span>}
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Nachname *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  style={errors.lastName ? errorInputStyle : inputStyle}
                  disabled={loading}
                  placeholder="Nachname eingeben"
                />
                {errors.lastName && <span style={errorTextStyle}>{errors.lastName}</span>}
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Personentyp *</label>
              <select
                value={formData.personType}
                onChange={(e) => handlePersonTypeChange(e.target.value as PersonType)}
                style={errors.personType ? errorInputStyle : inputStyle}
                disabled={loading}
              >
                {Object.values(PersonTypes).map(type => {
                  const info = getPersonTypeInfo(type);
                  return (
                    <option key={type} value={type}>
                      {info.icon} {info.label} - {info.description}
                    </option>
                  );
                })}
              </select>
              {errors.personType && <span style={errorTextStyle}>{errors.personType}</span>}
            </div>
          </div>

          {/* Driver-specific fields */}
          {isDriver && (
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>Führerschein-Informationen</h3>
              
              <div style={fieldStyle}>
                <label style={labelStyle}>Führerscheinklassen *</label>
                <div style={checkboxGroupStyle}>
                  {Object.values(LicenseClasses).map(licenseClass => {
                    const info = getLicenseClassInfo(licenseClass);
                    return (
                      <label key={licenseClass} style={checkboxLabelStyle}>
                        <input
                          type="checkbox"
                          checked={formData.licenseClasses.includes(licenseClass)}
                          onChange={(e) => handleLicenseClassChange(licenseClass, e.target.checked)}
                          style={checkboxStyle}
                          disabled={loading}
                        />
                        <span style={checkboxTextStyle}>
                          {info.label} - {info.description}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {errors.licenseClasses && <span style={errorTextStyle}>{errors.licenseClasses}</span>}
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Führerschein gültig bis</label>
                <input
                  type="date"
                  value={formData.licenseExpiry}
                  onChange={(e) => setFormData(prev => ({ ...prev, licenseExpiry: e.target.value }))}
                  style={errors.licenseExpiry ? errorInputStyle : inputStyle}
                  disabled={loading}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.licenseExpiry && <span style={errorTextStyle}>{errors.licenseExpiry}</span>}
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Zusätzliche Informationen</h3>
            
            <div style={fieldStyle}>
              <label style={labelStyle}>Notizen</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                style={textareaStyle}
                disabled={loading}
                placeholder="Optionale Notizen..."
                rows={3}
              />
            </div>

            {isEditing && (
              <div style={fieldStyle}>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    style={checkboxStyle}
                    disabled={loading}
                  />
                  <span style={checkboxTextStyle}>Aktiv</span>
                </label>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={actionsStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={cancelButtonStyle}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={loading ? disabledSaveButtonStyle : saveButtonStyle}
              disabled={loading}
            >
              {loading ? 'Speichern...' : (isEditing ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  width: '100%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1.5rem',
  borderBottom: '1px solid #e9ecef',
  backgroundColor: '#f8f9fa',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#2c3e50',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#6c757d',
  padding: '0.5rem',
  borderRadius: '4px',
  transition: 'color 0.2s ease',
};

const formStyle: React.CSSProperties = {
  padding: '1.5rem',
  overflow: 'auto',
  flex: 1,
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: '600',
  color: '#495057',
  marginBottom: '1rem',
  paddingBottom: '0.5rem',
  borderBottom: '1px solid #e9ecef',
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
};

const fieldStyle: React.CSSProperties = {
  marginBottom: '1rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.5rem',
  fontSize: '0.9rem',
  fontWeight: '500',
  color: '#495057',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  border: '2px solid #e9ecef',
  borderRadius: '6px',
  fontSize: '1rem',
  transition: 'border-color 0.2s ease',
  boxSizing: 'border-box',
};

const errorInputStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: '#dc3545',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
};

const checkboxGroupStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '0.5rem',
  padding: '0.5rem',
  border: '1px solid #e9ecef',
  borderRadius: '6px',
  backgroundColor: '#f8f9fa',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  cursor: 'pointer',
  fontSize: '0.9rem',
};

const checkboxStyle: React.CSSProperties = {
  width: '16px',
  height: '16px',
  cursor: 'pointer',
};

const checkboxTextStyle: React.CSSProperties = {
  color: '#495057',
};

const errorTextStyle: React.CSSProperties = {
  color: '#dc3545',
  fontSize: '0.875rem',
  marginTop: '0.25rem',
  display: 'block',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'flex-end',
  paddingTop: '1rem',
  borderTop: '1px solid #e9ecef',
  marginTop: '1rem',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: '2px solid #6c757d',
  backgroundColor: 'transparent',
  color: '#6c757d',
  borderRadius: '6px',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

const saveButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  backgroundColor: '#28a745',
  color: 'white',
  borderRadius: '6px',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

const disabledSaveButtonStyle: React.CSSProperties = {
  ...saveButtonStyle,
  backgroundColor: '#6c757d',
  cursor: 'not-allowed',
};

export default TravelerForm;
